/**
 * UUID generation for the /uuid tool.
 *
 * Randomness comes from `crypto.getRandomValues` — the platform's CSPRNG —
 * rather than `Math.random()`, because these values end up as primary keys and
 * idempotency keys. Both versions are assembled from raw bytes here instead of
 * delegating to `crypto.randomUUID`, which exists only for v4 and is missing on
 * pages that are not served over a secure context.
 */

export type UuidVersion = 'v4' | 'v7';

export interface UuidFormat {
  /** A-F instead of a-f. */
  uppercase?: boolean;
  /** Strip the dashes (the "N" format). */
  hyphens?: boolean;
  /** Wrap in braces, as .NET and COM tooling expect. */
  braces?: boolean;
}

/** The site's supported versions, with the copy the picker renders. */
export const UUID_VERSIONS: Array<{ value: UuidVersion; label: string; hint: string }> = [
  { value: 'v4', label: 'Version 4 (random)', hint: '122 random bits. Leaks nothing, sorts randomly.' },
  {
    value: 'v7',
    label: 'Version 7 (time-ordered)',
    hint: 'Millisecond timestamp prefix. Sorts by creation time, kinder to database indexes.',
  },
];

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  const source = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (!source || typeof source.getRandomValues !== 'function') {
    throw new Error('This browser does not expose a cryptographic random source.');
  }
  source.getRandomValues(bytes);
  return bytes;
}

/** 16 bytes -> the canonical 8-4-4-4-12 hyphenated form. */
function bytesToUuid(bytes: Uint8Array): string {
  const hex: string[] = [];
  for (let i = 0; i < bytes.length; i += 1) {
    hex.push(bytes[i].toString(16).padStart(2, '0'));
  }
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-');
}

/** Stamp the RFC 4122 version nibble and the two variant bits in place. */
function setVersionAndVariant(bytes: Uint8Array, version: number): void {
  bytes[6] = (bytes[6] & 0x0f) | (version << 4);
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
}

/** RFC 4122 version 4: random everywhere except the version and variant bits. */
export function uuidV4(): string {
  const bytes = randomBytes(16);
  setVersionAndVariant(bytes, 4);
  return bytesToUuid(bytes);
}

/**
 * RFC 9562 version 7: a 48-bit big-endian millisecond timestamp followed by
 * random bits, so lexical order matches creation order.
 */
export function uuidV7(now: number = Date.now()): string {
  const bytes = randomBytes(16);
  const timestamp = Math.floor(now);
  for (let i = 0; i < 6; i += 1) {
    // Byte 0 holds the most significant 8 bits of the 48-bit timestamp.
    bytes[i] = Math.floor(timestamp / 2 ** (8 * (5 - i))) & 0xff;
  }
  setVersionAndVariant(bytes, 7);
  return bytesToUuid(bytes);
}

export function generateUuid(version: UuidVersion, now?: number): string {
  return version === 'v7' ? uuidV7(now) : uuidV4();
}

/** Apply the display options to a canonical UUID. */
export function formatUuid(uuid: string, format: UuidFormat = {}): string {
  const { uppercase = false, hyphens = true, braces = false } = format;
  let value = hyphens ? uuid : uuid.replace(/-/g, '');
  if (uppercase) {
    value = value.toUpperCase();
  }
  return braces ? `{${value}}` : value;
}

/** The most this tool will mint in one go, so a fat-fingered count cannot hang the tab. */
export const MAX_UUID_COUNT = 500;

/** `count` UUIDs of `version`, already formatted. */
export function generateUuids(
  count: number,
  version: UuidVersion,
  format: UuidFormat = {},
): string[] {
  const total = Math.min(Math.max(Math.floor(count) || 1, 1), MAX_UUID_COUNT);
  const values: string[] = [];
  for (let i = 0; i < total; i += 1) {
    values.push(formatUuid(generateUuid(version), format));
  }
  return values;
}
